/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { InputAdornment } from '@mui/material';
import type {
  DateTimePickerProps as MuiDateTimePickerProps,
  DateTimePickerSlotProps,
} from '@mui/x-date-pickers';
import { DateTimePicker as MuiDateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import React from 'react';
import { CalendarToday } from '@opengeoweb/theme';
import { dateUtils } from '../../utils';

export const utcToPickerDate = (value: Date): Date =>
  new Date(
    value.getUTCFullYear(),
    value.getUTCMonth(),
    value.getUTCDate(),
    value.getUTCHours(),
    value.getUTCMinutes(),
    0, // seconds to zero
    0, // milliseconds to zero
  );

export const pickerDateToUTC = (value: Date): Date =>
  new Date(
    Date.UTC(
      value.getFullYear(),
      value.getMonth(),
      value.getDate(),
      value.getHours(),
      value.getMinutes(),
      0, // seconds to zero
      0, // milliseconds to zero
    ),
  );

const OpenPickerIcon = (): React.ReactElement => <CalendarToday />;

export interface DateTimePickerProps extends MuiDateTimePickerProps {
  showUTCAdornment?: boolean;
}

type StaticSlotProps<T> = Exclude<
  T,
  ((...args: never[]) => unknown) | undefined
>;

type FieldSlotProps = StaticSlotProps<DateTimePickerSlotProps['field']>;
type OpenPickerButtonSlotProps = StaticSlotProps<
  DateTimePickerSlotProps['openPickerButton']
>;
type TextFieldSlotProps = StaticSlotProps<DateTimePickerSlotProps['textField']>;

const mergeFieldSlotProps = (
  slotProps: FieldSlotProps | undefined,
): FieldSlotProps => ({
  openPickerButtonPosition: 'start',
  ...slotProps,
});

const mergeOpenPickerButtonSlotProps = (
  slotProps: OpenPickerButtonSlotProps | undefined,
): OpenPickerButtonSlotProps => ({
  size: 'small',
  sx: { padding: 0 },
  ...slotProps,
});

const mergeTextFieldSlotProps = (
  slotProps: TextFieldSlotProps | undefined,
  showUTCAdornment: boolean,
): TextFieldSlotProps => ({
  variant: 'filled',
  ...(showUTCAdornment && {
    endAdornment: (
      <InputAdornment
        position="end"
        sx={{
          paddingTop: '16px',
        }}
      >
        UTC
      </InputAdornment>
    ),
  }),
  ...slotProps,
  slotProps: {
    ...slotProps?.slotProps,
    input: {
      ...slotProps?.slotProps?.input,
    },
  },
});

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  defaultValue,
  minDateTime,
  maxDateTime,
  onAccept,
  onChange,
  showUTCAdornment = true,
  slots,
  slotProps,
  ...props
}) => {
  const callerFieldSlotProps = slotProps?.field;
  const callerOpenPickerButtonSlotProps = slotProps?.openPickerButton;
  const callerTextFieldSlotProps = slotProps?.textField;

  const fieldSlotProps: DateTimePickerSlotProps['field'] =
    typeof callerFieldSlotProps === 'function'
      ? (ownerState): FieldSlotProps =>
          mergeFieldSlotProps(callerFieldSlotProps(ownerState))
      : mergeFieldSlotProps(callerFieldSlotProps);
  const openPickerButtonSlotProps: DateTimePickerSlotProps['openPickerButton'] =
    typeof callerOpenPickerButtonSlotProps === 'function'
      ? (ownerState): OpenPickerButtonSlotProps =>
          mergeOpenPickerButtonSlotProps(
            callerOpenPickerButtonSlotProps(ownerState),
          )
      : mergeOpenPickerButtonSlotProps(callerOpenPickerButtonSlotProps);
  const textFieldSlotProps: DateTimePickerSlotProps['textField'] =
    typeof callerTextFieldSlotProps === 'function'
      ? (ownerState): TextFieldSlotProps =>
          mergeTextFieldSlotProps(
            callerTextFieldSlotProps(ownerState),
            showUTCAdornment,
          )
      : mergeTextFieldSlotProps(callerTextFieldSlotProps, showUTCAdornment);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <MuiDateTimePicker
        ampm={false}
        desktopModeMediaQuery="@media (min-width: 720px)"
        format={dateUtils.DATE_FORMAT_DATEPICKER}
        timeSteps={{ minutes: 1 }}
        {...props}
        defaultValue={defaultValue ? utcToPickerDate(defaultValue) : undefined}
        maxDateTime={maxDateTime ? utcToPickerDate(maxDateTime) : undefined}
        minDateTime={minDateTime ? utcToPickerDate(minDateTime) : undefined}
        onAccept={
          onAccept
            ? (acceptedValue, context): void =>
                onAccept(
                  acceptedValue ? pickerDateToUTC(acceptedValue) : null,
                  context,
                )
            : undefined
        }
        onChange={
          onChange
            ? (changedValue, context): void =>
                onChange(
                  changedValue ? pickerDateToUTC(changedValue) : null,
                  context,
                )
            : undefined
        }
        slotProps={{
          ...slotProps,
          field: fieldSlotProps,
          openPickerButton: openPickerButtonSlotProps,
          textField: textFieldSlotProps,
        }}
        slots={{ openPickerIcon: OpenPickerIcon, ...slots }}
        value={value ? utcToPickerDate(value) : value}
      />
    </LocalizationProvider>
  );
};
